import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiDownload,
  FiEdit2,
  FiExternalLink,
  FiLink,
  FiPlus,
  FiRefreshCw,
  FiTrash2,
  FiX
} from 'react-icons/fi';
import AdminLogin from '../components/AdminLogin';
import api from '../services/api';
import './CertificatesPage.css';

const ADMIN_PASSWORD = 'VikramDyD@0720';

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const loadImageFromFile = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = (error) => {
      URL.revokeObjectURL(objectUrl);
      reject(error);
    };
    img.src = objectUrl;
  });

const canvasToBlob = (canvas, mimeType, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Unable to process image'));
        return;
      }
      resolve(blob);
    }, mimeType, quality);
  });

const optimizeCertificateImage = async (file) => {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please upload a valid image file.');
  }

  const image = await loadImageFromFile(file);
  const maxDimension = 1800;
  const ratio = Math.min(1, maxDimension / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.width * ratio));
  canvas.height = Math.max(1, Math.round(image.height * ratio));

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Unable to process image in this browser.');
  }
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  let quality = 0.85;
  let blob = await canvasToBlob(canvas, 'image/jpeg', quality);
  const maxBytes = 2 * 1024 * 1024;

  while (blob.size > maxBytes && quality > 0.4) {
    quality -= 0.1;
    blob = await canvasToBlob(canvas, 'image/jpeg', quality);
  }

  if (blob.size > maxBytes) {
    throw new Error('Image is too large. Please upload an image below 2 MB.');
  }

  const optimizedFile = new File([blob], `${file.name.replace(/\.[^/.]+$/, '') || 'certificate'}.jpg`, {
    type: 'image/jpeg'
  });

  return fileToBase64(optimizedFile);
};

const downloadByUrl = async (url, filename) => {
  const response = await fetch(url);
  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(objectUrl);
};

const CertificatesPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeCertificate, setActiveCertificate] = useState(null);
  const [createResult, setCreateResult] = useState(null);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [createForm, setCreateForm] = useState({
    name: '',
    course: '',
    duration: '',
    file: null
  });

  const [editForm, setEditForm] = useState({
    name: '',
    course: '',
    duration: '',
    file: null
  });

  useEffect(() => {
    const savedAuth = window.sessionStorage.getItem('dylora_admin_auth');
    if (savedAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCertificates();
    }
  }, [isAuthenticated]);

  const fetchCertificates = async () => {
    setLoading(true);
    try {
      const response = await api.getAllCertificates(ADMIN_PASSWORD);
      setCertificates(response.data || []);
    } catch (error) {
      console.error('Fetch certificates error:', error);
      setCertificates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    window.sessionStorage.setItem('dylora_admin_auth', 'true');
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    window.sessionStorage.removeItem('dylora_admin_auth');
    setIsAuthenticated(false);
    setCertificates([]);
    navigate('/dipadminv');
  };

  const openCreateModal = () => {
    setCreateForm({ name: '', course: '', duration: '', file: null });
    setCreateResult(null);
    setFormError('');
    setShowCreateModal(true);
  };

  const openEditModal = (certificate) => {
    setActiveCertificate(certificate);
    setEditForm({
      name: certificate.name,
      course: certificate.course,
      duration: certificate.duration,
      file: null
    });
    setFormError('');
    setShowEditModal(true);
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setFormError('');

    if (!createForm.name || !createForm.course || !createForm.duration || !createForm.file) {
      setFormError('All fields including certificate image are required.');
      return;
    }

    setSubmitting(true);
    try {
      const certificateImageBase64 = await optimizeCertificateImage(createForm.file);
      const response = await api.createCertificate(
        {
          name: createForm.name,
          course: createForm.course,
          duration: createForm.duration,
          certificateImageBase64
        },
        ADMIN_PASSWORD
      );

      setCreateResult(response.data);
      await fetchCertificates();
    } catch (error) {
      console.error('Create certificate error:', error);
      setFormError(error?.response?.data?.message || error?.message || 'Failed to create certificate');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    if (!activeCertificate) {
      return;
    }

    setFormError('');
    setSubmitting(true);

    try {
      const payload = {
        name: editForm.name,
        course: editForm.course,
        duration: editForm.duration
      };

      if (editForm.file) {
        payload.certificateImageBase64 = await optimizeCertificateImage(editForm.file);
      }

      await api.updateCertificate(activeCertificate._id, payload, ADMIN_PASSWORD);
      setShowEditModal(false);
      setActiveCertificate(null);
      await fetchCertificates();
    } catch (error) {
      console.error('Update certificate error:', error);
      setFormError(error?.response?.data?.message || error?.message || 'Failed to update certificate');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (certificate) => {
    const confirmed = window.confirm(`Delete certificate ${certificate.certificateId} for ${certificate.name}?`);
    if (!confirmed) {
      return;
    }

    try {
      await api.deleteCertificate(certificate._id, ADMIN_PASSWORD);
      await fetchCertificates();
    } catch (error) {
      console.error('Delete certificate error:', error);
      window.alert(error?.response?.data?.message || 'Failed to delete certificate');
    }
  };

  const sortedCertificates = useMemo(
    () => [...certificates].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [certificates]
  );

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="cert-page">
      <header className="cert-header">
        <div>
          <h1>Certificate Management</h1>
          <p>Generate, manage and verify internship certificates.</p>
        </div>
        <div className="cert-header-actions">
          <button className="cert-btn cert-btn-outline" onClick={() => navigate('/dipadminv')}>
            <FiArrowLeft /> Dashboard
          </button>
          <button className="cert-btn cert-btn-outline" onClick={fetchCertificates}>
            <FiRefreshCw /> Refresh
          </button>
          <button className="cert-btn cert-btn-primary" onClick={openCreateModal}>
            <FiPlus /> Add Certificate
          </button>
          <button className="cert-btn cert-btn-outline" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <section className="cert-table-card">
        {loading ? (
          <div className="cert-empty">Loading certificates...</div>
        ) : sortedCertificates.length === 0 ? (
          <div className="cert-empty">
            <h3>No certificates yet</h3>
            <p>Create the first certificate to generate QR and verification links.</p>
          </div>
        ) : (
          <div className="cert-table-wrap">
            <table className="cert-table">
              <thead>
                <tr>
                  <th>Actions</th>
                  <th>Certificate ID</th>
                  <th>Name</th>
                  <th>Course</th>
                  <th>Duration</th>
                  <th>Verification ID</th>
                  <th>QR Code</th>
                  <th>Verification Link</th>
                </tr>
              </thead>
              <tbody>
                {sortedCertificates.map((certificate) => (
                  <tr key={certificate._id}>
                    <td>
                      <div className="action-stack">
                        <button className="row-btn" onClick={() => openEditModal(certificate)}>
                          <FiEdit2 /> Update
                        </button>
                        <button className="row-btn danger" onClick={() => handleDelete(certificate)}>
                          <FiTrash2 /> Delete
                        </button>
                      </div>
                    </td>
                    <td className="mono">{certificate.certificateId}</td>
                    <td>{certificate.name}</td>
                    <td>{certificate.course}</td>
                    <td>{certificate.duration}</td>
                    <td className="mono">{certificate.verificationCode}</td>
                    <td>
                      <div className="qr-box">
                        <img src={certificate.qrCodeUrl} alt={`QR for ${certificate.verificationCode}`} />
                        <button
                          className="mini-link"
                          onClick={() => downloadByUrl(certificate.qrCodeUrl, `qr-${certificate.verificationCode}.png`)}
                        >
                          <FiDownload /> Download QR
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="link-stack">
                        <a href={certificate.verificationLink} target="_blank" rel="noreferrer">
                          <FiExternalLink /> Open Link
                        </a>
                        <button
                          className="mini-link"
                          onClick={async () => {
                            await navigator.clipboard.writeText(certificate.verificationLink);
                            window.alert('Verification link copied');
                          }}
                        >
                          <FiLink /> Copy Link
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showCreateModal && (
        <div className="cert-modal-backdrop">
          <div className="cert-modal">
            <button className="close-btn" onClick={() => setShowCreateModal(false)}>
              <FiX />
            </button>
            <h2>Add Certificate</h2>
            <form onSubmit={handleCreate} className="cert-form">
              <label>
                Name
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, name: event.target.value }))}
                />
              </label>
              <label>
                Course
                <input
                  type="text"
                  value={createForm.course}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, course: event.target.value }))}
                />
              </label>
              <label>
                Duration
                <input
                  type="text"
                  placeholder="Example: 3 Months"
                  value={createForm.duration}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, duration: event.target.value }))}
                />
              </label>
              <label>
                Certificate Image
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, file: event.target.files?.[0] || null }))}
                />
              </label>

              {formError && <p className="form-error">{formError}</p>}

              <button type="submit" className="cert-btn cert-btn-primary" disabled={submitting}>
                {submitting ? 'Generating...' : 'Add & Generate QR'}
              </button>
            </form>

            {createResult && (
              <div className="create-result">
                <h3>Generated Successfully</h3>
                <p><strong>Certificate ID:</strong> {createResult.certificateId}</p>
                <p><strong>Verification Code:</strong> {createResult.verificationCode}</p>
                <p>
                  <strong>Verification Link:</strong>{' '}
                  <a href={createResult.verificationLink} target="_blank" rel="noreferrer">
                    Open Certificate Page
                  </a>
                </p>
                <div className="result-qr">
                  <img src={createResult.qrCodeUrl} alt="Generated QR" />
                  <button
                    className="cert-btn cert-btn-outline"
                    onClick={() => downloadByUrl(createResult.qrCodeUrl, `qr-${createResult.verificationCode}.png`)}
                  >
                    <FiDownload /> Download QR Image
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="cert-modal-backdrop">
          <div className="cert-modal">
            <button className="close-btn" onClick={() => setShowEditModal(false)}>
              <FiX />
            </button>
            <h2>Update Certificate</h2>
            <form onSubmit={handleUpdate} className="cert-form">
              <label>
                Name
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, name: event.target.value }))}
                />
              </label>
              <label>
                Course
                <input
                  type="text"
                  value={editForm.course}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, course: event.target.value }))}
                />
              </label>
              <label>
                Duration
                <input
                  type="text"
                  value={editForm.duration}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, duration: event.target.value }))}
                />
              </label>
              <label>
                New Certificate Image (optional)
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => setEditForm((prev) => ({ ...prev, file: event.target.files?.[0] || null }))}
                />
              </label>

              {formError && <p className="form-error">{formError}</p>}

              <button type="submit" className="cert-btn cert-btn-primary" disabled={submitting}>
                {submitting ? 'Updating...' : 'Update Certificate'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificatesPage;
